import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AssetManager from '../../services/AssetManager';
import DraftManager from '../../services/DraftManager';
import AcademicYearService from '../../services/AcademicYearService';
import PeriodManager from '../../services/PeriodManager.ts';
import logger from '../../utils/logger';
import ScheduleEntry from '../../models/ScheduleEntry';
import { conflictManager } from '../../services/ConflictManager';
import useScheduleSaveManager from '../../hooks/useScheduleSaveManager.ts';
import { getLastSelectedPeriodId, pickInitialPeriod, setLastSelectedPeriodId } from '../../utils/classSchedulerUiState';

// --- Persistence Constants & Helpers ---
const DRAFT_STATE_STORAGE_KEY = 'pi-schedule-draft-state';

// Helper function to safely access localStorage
const safeLocalStorage = {
    getItem: (key) => {
        try {
            return localStorage.getItem(key);
        } catch (error) {
            console.warn('localStorage not available:', error);
            return null;
        }
    },
    setItem: (key, value) => {
        try {
            localStorage.setItem(key, value);
            return true;
        } catch (error) {
            console.warn('localStorage not available for writing:', error);
            return false;
        }
    },
    removeItem: (key) => {
        try {
            localStorage.removeItem(key);
            return true;
        } catch (error) {
            console.warn('localStorage not available for removal:', error);
            return false;
        }
    }
};

export const useClassSchedulerState = (user) => {
    const { token } = useAuth();

    const academicYearService = useMemo(() => AcademicYearService.getInstance(), []);

    // Academic-year selection:
    // `selectedYearForView` represents the academic year START calendar year (e.g. 2025 for "2025-2026").
    // Using `new Date().getFullYear()` breaks in Jan because calendar year flips before the academic year does.
    const initialSelectedYearForViewRef = useRef(null);
    const didAutoSyncYearRef = useRef(false);
    const getDefaultAcademicYearStartYear = useCallback(() => {
        // First choice: use AcademicYearService cached parsed year (if already fetched elsewhere).
        const parsed = academicYearService.getParsedAcademicYear?.();
        if (parsed && Number.isFinite(parsed.startCalendarYear)) {
            return parsed.startCalendarYear;
        }

        // Fallback (only until service fetch completes): assume academic year starts in September.
        const now = new Date();
        const calendarYear = now.getFullYear();
        const month = now.getMonth(); // 0=Jan ... 11=Dec
        const ACADEMIC_YEAR_START_MONTH = 8; // September
        return month >= ACADEMIC_YEAR_START_MONTH ? calendarYear : calendarYear - 1;
    }, [academicYearService]);

    // --- Services ---
    const assetManager = useMemo(() => AssetManager.getInstance(), []);
    const periodManager = useMemo(() => PeriodManager.getInstance(), []);
    const draftManager = useMemo(() => new DraftManager({ getToken: () => token }), [token]);

    // Initialize state from localStorage to prevent flickering between live and draft on reload
    const [isLiveMode, setIsLiveMode] = useState(() => {
        try {
            const savedState = safeLocalStorage.getItem(DRAFT_STATE_STORAGE_KEY);
            if (savedState) {
                const parsedState = JSON.parse(savedState);
                logger.debug('[useClassSchedulerState] Initializing in draft-centric mode', {
                    savedDraft: parsedState.currentDraft || 'none'
                });
            }
        } catch (error) {
            console.warn('[useClassSchedulerState] Error reading initial state from localStorage:', error);
        }
        return false;
    });

    const [currentDraftName, setCurrentDraftName] = useState(() => {
        try {
            const savedState = safeLocalStorage.getItem(DRAFT_STATE_STORAGE_KEY);
            if (savedState) {
                const parsedState = JSON.parse(savedState);
                logger.debug('[useClassSchedulerState] Initializing with saved draft:', parsedState.currentDraft || 'none');
                return parsedState.currentDraft || '';
            }
        } catch (error) {
            console.warn('[useClassSchedulerState] Error reading initial draft name from localStorage:', error);
        }
        logger.debug('[useClassSchedulerState] Initializing with no draft');
        return ''; // Default to empty
    });

    // Schedule data
    const [courseDefinitions, setCourseDefinitions] = useState([]);
    const [isFetchingDefinitions, setIsFetchingDefinitions] = useState(true);
    const [fetchDefinitionsError, setFetchDefinitionsError] = useState(null);
    // Track if we've successfully loaded definitions at least once to avoid full-screen loading flashes on refetch
    const [hasLoadedDefinitionsOnce, setHasLoadedDefinitionsOnce] = useState(false);

    // Asset data
    const [teachers, setTeachers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [isLoadingAssets, setIsLoadingAssets] = useState(true);
    const [assetsError, setAssetsError] = useState(null);

    // Time periods and year structure
    const [timePeriods, setTimePeriods] = useState([]);
    const [selectedPeriodId, setSelectedPeriodId] = useState(() => getLastSelectedPeriodId());
    const [isFetchingPeriods, setIsFetchingPeriods] = useState(true);
    const [fetchPeriodsError, setFetchPeriodsError] = useState(null);
    const [yearStructure, setYearStructure] = useState(null);
    const [isFetchingYearStructure, setIsFetchingYearStructure] = useState(true);

    // Schedule instances and filtering
    const [scheduledInstances, setScheduledInstances] = useState([]);
    const [filteredScheduledInstances, setFilteredScheduledInstances] = useState([]);
    const [timelineViewRange, setTimelineViewRange] = useState(null);
    const [textFilterQuery, setTextFilterQuery] = useState('');

    // View and selection state
    const [currentView, setCurrentView] = useState('calendar');
    const [selectedRoomForView, setSelectedRoomForView] = useState(null);
    const [selectedTeacherForView, setSelectedTeacherForView] = useState(null);
    const [selectedYearForView, setSelectedYearForView] = useState(() => {
        const initial = getDefaultAcademicYearStartYear();
        initialSelectedYearForViewRef.current = initial;
        return initial;
    });
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedWeek, setSelectedWeek] = useState(null);

    // Keep a ref synced so async effects can read the latest selected year without re-running.
    const selectedYearForViewRef = useRef(selectedYearForView);
    useEffect(() => {
        selectedYearForViewRef.current = selectedYearForView;
    }, [selectedYearForView]);

    // Draft management
    const [draftRefreshTrigger, setDraftRefreshTrigger] = useState(0);
    const [isDraftManagerInitialized, setIsDraftManagerInitialized] = useState(false);

    // Change tracking state
    const [isBaselineSet, setIsBaselineSet] = useState(false);
    const [hasInitialConflictsCalculated, setHasInitialConflictsCalculated] = useState(false);

    // Loading and error states
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Import/export states
    const [isImporting, setIsImporting] = useState(false);
    const [importError, setImportError] = useState(null);
    const [importSuccess, setImportSuccess] = useState(null);
    const [isExporting, setIsExporting] = useState(false);

    // Debug state
    const [isDebugDrawerOpen, setIsDebugDrawerOpen] = useState(false);

    // Local ID swap handler: replace temp IDs with server IDs in working list
    const onIdSwap = useCallback((tempId, savedEntry) => {
        try {
            const tempKey = String(tempId);
            const replacement = (savedEntry && typeof savedEntry.toObject === 'function') ? savedEntry : new ScheduleEntry(savedEntry);
            setScheduledInstances(prev => {
                let replaced = false;
                const updated = prev.map(item => {
                    if (String(item?.id) === tempKey) {
                        replaced = true;
                        return replacement;
                    }
                    return item;
                });
                return replaced ? updated : prev;
            });
            logger.debug('[useClassSchedulerState] onIdSwap applied', { tempId: tempKey, newId: String(replacement?.id) });
        } catch (e) {
            logger.warn('[useClassSchedulerState] onIdSwap failed', e);
        }
    }, [setScheduledInstances]);

    // --- Save Manager Integration (baseline + persistence) ---
    const {
        changeLog,
        addLogEntry,
        calculateNetChanges,
        handleSaveChanges,
        serverBaseline,
        initializeChangeTracking,
        saveSingleEntry,
        saveChangesToAPI,
        isFetchingBaseline,
        baselineError,
        baselineVersion,
        getChangeIdCounts,
        hasPendingChangesByIdSets,
        // Undo/Redo functionality
        performUndo,
        performRedo,
        canUndo,
        canRedo,
        getUndoRedoState
    } = useScheduleSaveManager(user, {
        isLiveMode,
        draftName: currentDraftName,
        selectedYear: selectedYearForView,
        selectedPeriodId,
        selectedMonth,
        selectedWeek,
        onIdSwap
    });

    // Initialize DraftManager with current state and verify draft existence on mount
    useEffect(() => {
        const initializeDraftManager = async () => {
            try {
                await draftManager.fetchDrafts();

                if (currentDraftName) {
                    const draftExists = draftManager.hasDraft(currentDraftName);

                    if (draftExists) {
                        draftManager.setDraftMode(currentDraftName);
                        logger.debug('[useClassSchedulerState] DraftManager initialized with draft:', currentDraftName);
                    } else {
                        logger.warn('[useClassSchedulerState] Saved draft no longer exists:', currentDraftName);
                        setCurrentDraftName('');
                        setError(`Draft "${currentDraftName}" no longer exists. Loading another draft...`);
                        setTimeout(() => setError(null), 5000);
                    }
                } else {
                    logger.debug('[useClassSchedulerState] No saved draft; waiting for active-draft auto-load');
                }
            } catch (error) {
                logger.error('[useClassSchedulerState] Error initializing DraftManager:', error);
                setCurrentDraftName('');
            } finally {
                setIsDraftManagerInitialized(true);
                logger.debug('[useClassSchedulerState] DraftManager initialization complete');
            }
        };

        initializeDraftManager();
    }, [draftManager, setError]);

    // Listen to DraftManager state changes and persist to localStorage
    useEffect(() => {
        if (!isDraftManagerInitialized) {
            return;
        }

        const handleDraftStateChange = (state) => {
            logger.debug('[useClassSchedulerState] DraftManager state changed:', state);

            // Normalize draft name so \"no draft\" is consistent across app (treat null/undefined as empty string)
            const normalizedDraftName = state?.currentDraft ?? '';

            const saveSuccess = safeLocalStorage.setItem(DRAFT_STATE_STORAGE_KEY, JSON.stringify({
                isLiveMode: state.isLiveMode,
                currentDraft: normalizedDraftName
            }));
            if (saveSuccess) {
                logger.debug('[useClassSchedulerState] Saved state to localStorage');
            }

            if (state.isLiveMode !== isLiveMode || normalizedDraftName !== currentDraftName) {
                logger.debug('[useClassSchedulerState] Updating mode from DraftManager:', {
                    oldMode: { isLiveMode, currentDraftName },
                    newMode: { isLiveMode: state.isLiveMode, currentDraft: normalizedDraftName }
                });
                setIsLiveMode(state.isLiveMode);
                setCurrentDraftName(normalizedDraftName);
                // Clear current working schedule so the new document can seed fresh data
                setScheduledInstances([]);
                setFilteredScheduledInstances([]);
                setCourseDefinitions([]); // Clear old definitions to prevent re-seeding with stale data
                setIsFetchingDefinitions(true); // Immediately mark as fetching to prevent premature rendering
                setDraftRefreshTrigger(prev => prev + 1);
                setIsBaselineSet(false);
                setHasInitialConflictsCalculated(false);
            }
        };

        draftManager.addListener(handleDraftStateChange);

        return () => {
            draftManager.removeListener(handleDraftStateChange);
        };
    }, [draftManager, isLiveMode, currentDraftName, DRAFT_STATE_STORAGE_KEY, safeLocalStorage, isDraftManagerInitialized]);

    // Fetch all asset data
    useEffect(() => {
        const fetchAllAssets = async () => {
            setIsLoadingAssets(true);
            setAssetsError(null);
            try {
                logger.debug('[useClassSchedulerState] Fetching all assets...');

                const [teachersData, groupsData, subjectsData, roomsData] = await Promise.all([
                    assetManager.fetchTeachers(),
                    assetManager.fetchGroups(),
                    assetManager.fetchSubjects(),
                    assetManager.fetchRooms()
                ]);

                logger.debug('[useClassSchedulerState] Fetched assets:', {
                    teachers: teachersData.length,
                    groups: groupsData.length,
                    subjects: subjectsData.length,
                    rooms: roomsData.length
                });

                setTeachers(teachersData);
                setGroups(groupsData);
                setSubjects(subjectsData);
                setRooms(roomsData);

            } catch (error) {
                logger.error('[useClassSchedulerState] Error fetching assets:', error);
                setAssetsError(error.message || 'Failed to load asset data.');
                setTeachers([]);
                setGroups([]);
                setSubjects([]);
                setRooms([]);
            } finally {
                setIsLoadingAssets(false);
            }
        };

        fetchAllAssets();
    }, [assetManager]);

    // Fetch periods
    useEffect(() => {
        const fetchPeriods = async () => {
            setIsFetchingPeriods(true);
            setFetchPeriodsError(null);
            try {
                const periods = await periodManager.fetchPeriods();
                logger.debug('[useClassSchedulerState] Fetched periods from API:', periods.length);
                setTimePeriods(periods);

                if (periods.length > 0) {
                    const initialPeriod = pickInitialPeriod(periods, getLastSelectedPeriodId());
                    logger.debug('[useClassSchedulerState] Setting initial period:', initialPeriod.name, 'ID:', initialPeriod.id);
                    setSelectedPeriodId(initialPeriod.id);
                } else {
                    console.warn('[useClassSchedulerState] No periods available');
                    setFetchPeriodsError('No periods found. You may need to set up academic years and periods first.');
                }

            } catch (error) {
                console.error('[useClassSchedulerState] Error fetching periods:', error);
                let errorMessage = error.message || 'Failed to load periods.';

                if (error.message && error.message.includes('academic year')) {
                    errorMessage = 'No academic years found. Please set up the academic year structure first in the Year Structure Editor.';
                } else if (error.message && error.message.includes('PROFILE_SETUP_REQUIRED')) {
                    errorMessage = 'Profile setup required. Please complete the initial setup.';
                } else if (error.message && error.message.includes('Unauthorized')) {
                    errorMessage = 'Authentication required. Please log in again.';
                }

                setFetchPeriodsError(errorMessage);
                setTimePeriods([]);
            } finally {
                setIsFetchingPeriods(false);
            }
        };

        fetchPeriods();
    }, [periodManager]);

    useEffect(() => {
        if (selectedPeriodId == null || selectedPeriodId === '') {
            return;
        }
        setLastSelectedPeriodId(selectedPeriodId);
    }, [selectedPeriodId]);

    const refreshTimePeriods = useCallback(async () => {
        try {
            const periods = await periodManager.refreshPeriods();
            setTimePeriods(periods);
            return periods;
        } catch (error) {
            console.error('[useClassSchedulerState] Error refreshing periods:', error);
            throw error;
        }
    }, [periodManager]);

    // Fetch year structure
    useEffect(() => {
        const fetchYearStructureData = async () => {
            setIsFetchingYearStructure(true);
            try {
                // Use AcademicYearService directly (it handles caching + avoids duplicate requests)
                const yearStructureData = await academicYearService.getAcademicYearStructure();
                if (!yearStructureData) {
                    throw new Error('No year structure available');
                }
                logger.debug('[useClassSchedulerState] Fetched year structure:', yearStructureData);
                setYearStructure(yearStructureData);

                // If the backend returns an academicYear string (e.g. "2025-2026"), prefer it for initial year selection.
                try {
                    const periods = yearStructureData.periods || [];
                    const fromPeriods = periods.length
                        ? Math.min(...periods.map(p => Number(p.startWeekYear ?? p.year)).filter(Number.isFinite))
                        : null;
                    const startYearStr = String(yearStructureData.academicYear || '').split('-')[0];
                    const startYearFromLabel = parseInt(startYearStr, 10);
                    const startYear = Number.isFinite(startYearFromLabel)
                        ? startYearFromLabel
                        : (fromPeriods != null ? fromPeriods : null);
                    const initialYear = initialSelectedYearForViewRef.current;
                    const currentSelected = selectedYearForViewRef.current;

                    if (!didAutoSyncYearRef.current &&
                        startYear != null &&
                        Number.isFinite(startYear) &&
                        initialYear != null &&
                        currentSelected === initialYear &&
                        currentSelected !== startYear
                    ) {
                        setSelectedYearForView(startYear);
                    }
                    didAutoSyncYearRef.current = true;
                } catch (_) { /* no-op */ }
            } catch (error) {
                console.error('[useClassSchedulerState] Error fetching year structure:', error);
                const fallbackYear = selectedYearForViewRef.current ?? new Date().getFullYear();
                setYearStructure({
                    startWeek: 1,
                    endWeek: 52,
                    academicYear: `${fallbackYear}-${fallbackYear + 1}`
                });
            } finally {
                setIsFetchingYearStructure(false);
            }
        };

        fetchYearStructureData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [academicYearService]);

    // Fetch initial course definitions - only when mode changes or on mount
    useEffect(() => {
        if (!isDraftManagerInitialized) {
            logger.debug('[useClassSchedulerState] Waiting for DraftManager initialization before fetching data...');
            return;
        }

        const fetchInitialEntries = async () => {
            setIsFetchingDefinitions(true);
            setFetchDefinitionsError(null);
            let skippedForMissingDraft = false;
            try {
                logger.debug('[useClassSchedulerState] Fetching course definitions...', { currentDraftName });

                if (!currentDraftName) {
                    logger.debug('[useClassSchedulerState] Waiting for draft selection before fetching entries');
                    skippedForMissingDraft = true;
                    return;
                }

                logger.debug('[useClassSchedulerState] Fetching DRAFT entries for:', currentDraftName);
                const entries = await assetManager.fetchScheduleEntriesForDraft(currentDraftName);

                const processedEntries = entries.map(e => {
                    const entryData = typeof e.toObject === 'function' ? e.toObject() : { ...e };
                    return new ScheduleEntry({
                        ...entryData,
                        weekSeries: entryData.weekSeries || ''
                    });
                });

                console.log('*** USECLASSCHEDULERSTATE - SETTING COURSE DEFINITIONS ***', {
                    processedEntriesLength: processedEntries?.length || 0,
                    sampleIds: processedEntries?.slice(0, 5).map(entry => entry.id) || []
                });

                setCourseDefinitions(processedEntries || []);
                // Replace working schedule immediately with the freshly fetched entries.
                // Do not set filteredScheduledInstances here — ClassSchedulerPage's view-only filter effect applies period/month/week.
                setScheduledInstances(processedEntries || []);

                logger.debug('[useClassSchedulerState] Processed entries:', processedEntries?.length || 0);

                // Mark as loaded at least once to prevent gating UI on subsequent background refetches
                if (!hasLoadedDefinitionsOnce) {
                    setHasLoadedDefinitionsOnce(true);
                }

            } catch (error) {
                console.log('*** USECLASSCHEDULERSTATE - ERROR SETTING COURSE DEFINITIONS ***', {
                    error: error.message,
                    settingEmptyArray: true
                });

                logger.error('[useClassSchedulerState] Error fetching all entries ON MOUNT:', error);
                setFetchDefinitionsError(error.message || 'Failed to load schedule data.');
                setCourseDefinitions([]);
            } finally {
                if (!skippedForMissingDraft) {
                    setIsFetchingDefinitions(false);
                }
            }
        };

        fetchInitialEntries();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- hasLoadedDefinitionsOnce is updated inside this effect; omitting avoids a duplicate fetch on first load.
    }, [assetManager, currentDraftName, draftRefreshTrigger, isDraftManagerInitialized, user?.role]);

    // Calculate conflicts when assets become available (separate from initial fetch)
    useEffect(() => {
        if (!hasInitialConflictsCalculated && 
            courseDefinitions.length > 0 && 
            teachers.length > 0 && 
            groups.length > 0 && 
            subjects.length > 0 && 
            user?.role === 'Admin') {
            
            logger.debug('[useClassSchedulerState] Calculating conflicts for loaded entries (separate effect)...');
            conflictManager.calculateConflictsForEntries(
                courseDefinitions.filter(entry => entry.isScheduled()),
                teachers,
                groups,
                subjects
            ).then(entriesWithConflicts => {
                const conflictMap = new Map(entriesWithConflicts.map(entry => [entry.id, entry]));
                const finalEntries = courseDefinitions.map(entry => {
                    const entryWithConflicts = conflictMap.get(entry.id);
                    return entryWithConflicts || entry;
                });
                console.log('*** USECLASSCHEDULERSTATE - CONFLICT CALCULATION COMPLETE ***', {
                    finalEntriesLength: finalEntries.length,
                    sampleIds: finalEntries.slice(0, 5).map(entry => entry.id)
                });

                setCourseDefinitions(finalEntries);
                setHasInitialConflictsCalculated(true);
                logger.debug('[useClassSchedulerState] Conflicts calculated and applied');
            }).catch(error => {
                logger.error('[useClassSchedulerState] Error calculating conflicts:', error);
                setHasInitialConflictsCalculated(true);
            });
        }
    }, [courseDefinitions, teachers, groups, subjects, user?.role, hasInitialConflictsCalculated]);

    // Get available academic years from the current academic-year context.
    const availableYears = useMemo(() => {
        const centerYear = selectedYearForViewRef.current || new Date().getFullYear();
        const minYear = centerYear - 1;
        const maxYear = centerYear + 2;

        const yearRange = [];
        for (let year = minYear; year <= maxYear; year++) {
            yearRange.push(year);
        }

        return yearRange;
    }, []);

    // Set default room for room view when rooms are available
    useEffect(() => {
        if (rooms.length > 0 && !selectedRoomForView) {
            setSelectedRoomForView(rooms[0].name || rooms[0].room_name);
        }
    }, [rooms, selectedRoomForView]);

    // Set default teacher for teacher view when teachers are available
    useEffect(() => {
        if (teachers.length > 0 && !selectedTeacherForView) {
            setSelectedTeacherForView(teachers[0].name);
        }
    }, [teachers, selectedTeacherForView]);

    // Set default year based on available data
    useEffect(() => {
        if (availableYears.length > 0 && !availableYears.includes(selectedYearForView)) {
            setSelectedYearForView(availableYears[0]);
        }
    }, [availableYears, selectedYearForView]);

    // Mode change handlers
    const handleModeChange = useCallback((liveMode, draftName) => {
        logger.debug('[useClassSchedulerState] Mode change:', { liveMode, draftName, currentMode: { isLiveMode, currentDraftName } });

        if (liveMode) {
            draftManager.setLiveMode();
        } else {
            draftManager.setDraftMode(draftName);
        }
    }, [draftManager, isLiveMode, currentDraftName]);

    const handleDraftChange = useCallback(() => {
        setDraftRefreshTrigger(prev => prev + 1);
    }, []);

    // --- Save helpers exposed by this state hook ---
    const SaveSchedule = useCallback(async (editedDataObj, originalItem) => {
        setIsLoading(true);
        setError(null);
        try {
            const saved = await saveSingleEntry(editedDataObj, originalItem);
            setIsBaselineSet(true);
            setSuccess('Entry saved successfully');
            setTimeout(() => setSuccess(null), 3000);
            return saved;
        } catch (e) {
            const message = e?.message || 'Failed to save entry';
            setError(message);
            throw e;
        } finally {
            setIsLoading(false);
        }
    }, [saveSingleEntry]);

    const SaveAll = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            await handleSaveChanges(scheduledInstances);
            setIsBaselineSet(true);
            setSuccess('All changes saved');
            setTimeout(() => setSuccess(null), 3000);
        } catch (e) {
            const message = e?.message || 'Failed to save changes';
            setError(message);
            throw e;
        } finally {
            setIsLoading(false);
        }
    }, [handleSaveChanges, scheduledInstances]);

    // Toggle debug drawer
    const toggleDebugDrawer = useCallback(() => {
        setIsDebugDrawerOpen(prev => !prev);
    }, []);

    // Undo/Redo wrapper functions that use current state
    const handleUndo = useCallback(() => {
        return performUndo(scheduledInstances, setScheduledInstances);
    }, [performUndo, scheduledInstances, setScheduledInstances]);

    const handleRedo = useCallback(() => {
        return performRedo(scheduledInstances, setScheduledInstances);
    }, [performRedo, scheduledInstances, setScheduledInstances]);

    // Background refresh: re-fetch entries from API without gating the UI
    const refreshScheduleEntries = useCallback(async () => {
        try {
            setIsFetchingDefinitions(true);
            setFetchDefinitionsError(null);

            if (!currentDraftName) {
                return;
            }

            const entries = await assetManager.fetchScheduleEntriesForDraft(currentDraftName);

            const processedEntries = (entries || []).map(e => {
                const entryData = typeof e.toObject === 'function' ? e.toObject() : { ...e };
                return new ScheduleEntry({
                    ...entryData,
                    weekSeries: entryData.weekSeries || ''
                });
            });

            setCourseDefinitions(processedEntries);
            setScheduledInstances(processedEntries);
            // Don't set filteredScheduledInstances here - let the filtering effect handle it
            // setFilteredScheduledInstances(processedEntries);
        } catch (error) {
            logger.error('[useClassSchedulerState] Background refresh failed:', error);
            setFetchDefinitionsError(error?.message || 'Failed to refresh schedule data.');
        } finally {
            setIsFetchingDefinitions(false);
        }
    }, [assetManager, currentDraftName]);

    const discardUnsavedChanges = useCallback(() => {
        const baseline = serverBaseline || [];
        const restored = baseline.map((entryData) => {
            const data = entryData && typeof entryData.toObject === 'function'
                ? entryData.toObject()
                : { ...entryData };
            return new ScheduleEntry({
                ...data,
                weekSeries: data.weekSeries || ''
            });
        });
        setScheduledInstances(restored);
        initializeChangeTracking(baseline);
    }, [serverBaseline, setScheduledInstances, initializeChangeTracking]);

    return {
        // Services
        assetManager,
        draftManager,

        // State
        isLiveMode,
        currentDraftName,
        courseDefinitions,
        isFetchingDefinitions,
        fetchDefinitionsError,
        hasLoadedDefinitionsOnce,
        teachers,
        groups,
        subjects,
        rooms,
        isLoadingAssets,
        assetsError,
        timePeriods,
        refreshTimePeriods,
        selectedPeriodId,
        setSelectedPeriodId,
        isFetchingPeriods,
        fetchPeriodsError,
        yearStructure,
        isFetchingYearStructure,
        scheduledInstances,
        setScheduledInstances,
        filteredScheduledInstances,
        setFilteredScheduledInstances,
        timelineViewRange,
        setTimelineViewRange,
        textFilterQuery,
        setTextFilterQuery,
        currentView,
        setCurrentView,
        selectedRoomForView,
        setSelectedRoomForView,
        selectedTeacherForView,
        setSelectedTeacherForView,
        selectedYearForView,
        setSelectedYearForView,
        selectedMonth,
        setSelectedMonth,
        selectedWeek,
        setSelectedWeek,
        draftRefreshTrigger,
        setDraftRefreshTrigger,
        isDraftManagerInitialized,
        isBaselineSet,
        setIsBaselineSet,
        hasInitialConflictsCalculated,
        setHasInitialConflictsCalculated,
        isLoading,
        setIsLoading,
        error,
        setError,
        success,
        setSuccess,
        isImporting,
        setIsImporting,
        importError,
        setImportError,
        importSuccess,
        setImportSuccess,
        isExporting,
        setIsExporting,
        isDebugDrawerOpen,
        setIsDebugDrawerOpen,
        
        // Save manager/baseline
        changeLog,
        calculateNetChanges,
        serverBaseline,
        isFetchingBaseline,
        baselineError,
        baselineVersion,
        getChangeIdCounts,
        hasPendingChangesByIdSets,

        // Computed values
        availableYears,

        // Handlers
        handleModeChange,
        handleDraftChange,
        toggleDebugDrawer,

        // Save actions
        SaveSchedule,
        SaveAll,

        // Background refresh
        refreshScheduleEntries,
        discardUnsavedChanges,

        // Backward-compat exports (to replace useScheduleController usage)
        addLogEntry,
        handleSaveChanges,
        saveChangesToAPI,
        handleSaveSchedule: saveSingleEntry,

        // Undo/Redo functionality
        handleUndo,
        handleRedo,
        canUndo,
        canRedo,
        getUndoRedoState,

        // Teacher management
        handleAddTeacher: async (teacherData) => {
            try {
                const newTeacher = await assetManager.createTeacher(teacherData);
                setTeachers(prev => [...prev, newTeacher]);
                return newTeacher;
            } catch (error) {
                logger.error('[useClassSchedulerState] Error adding teacher:', error);
                throw error;
            }
        },

        // Subject management
        handleAddSubject: async (subjectData) => {
            try {
                const newSubject = await assetManager.createSubject(subjectData);
                setSubjects(prev => [...prev, newSubject]);
                return newSubject; // Return the new subject so it can be selected
            } catch (error) {
                logger.error('[useClassSchedulerState] Error adding subject:', error);
                throw error;
            }
        },

        // Group management
        handleAddGroup: async (groupData) => {
            try {
                const newGroup = await assetManager.createGroup(groupData);
                setGroups(prev => [...prev, newGroup]);
                return newGroup; // Return the new group so it can be selected
            } catch (error) {
                logger.error('[useClassSchedulerState] Error adding group:', error);
                throw error;
            }
        }
    };
};
